import { cx } from "cva";
import { Fragment, useState } from "react";
import { MCPermissions } from "~/common/constants";
import { useAnalytics } from "~/components/Analytics";
import {
  Body,
  Link,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/ui-library";

export type DataAccessChipProps = {
  /** name of asset - this is only used for tooltip analytic events */
  assetName: string;
  /** name of related post - this is only used for tooltip analytic events */
  postName: string;
  requiredPermissions: MCPermissions[];
  userPermissions: MCPermissions[];
};

const permissionLabelMap = new Map([
  [MCPermissions.PRO_CONSUMER_PLUS, "Industry+"],
  [MCPermissions.PRO_ECONOMIC_PLUS, "Economic+"],
  [MCPermissions.PRO_POLITICAL_PLUS, "Political+"],
]);

type TooltipLabelProps = {
  permissions: MCPermissions[];
};

export const DataAccessTooltipLabel: React.FC<TooltipLabelProps> = ({
  permissions,
}) => {
  const permLabels = permissions
    .map((perm) => permissionLabelMap.get(perm))
    .filter(Boolean)
    .map((perm) => <strong key={perm}>{perm}</strong>);

  if (!permLabels.length) {
    return null;
  }

  if (permLabels.length === 1) {
    return permLabels[0];
  }

  if (permLabels.length === 2) {
    return (
      <>
        {permLabels[0]} or {permLabels[1]}
      </>
    );
  }

  const head = permLabels.slice(0, -2);
  const secondToLast = permLabels.at(-2)!;
  const last = permLabels.at(-1)!;

  const output = head
    .reduce((acc, value) => [...acc, value, ", "], [] as React.ReactNode[])
    .concat([secondToLast, " or ", last]);

  return <Fragment>{output}</Fragment>;
};

export const DataAccessChip: React.FC<DataAccessChipProps> = ({
  assetName,
  postName,
  requiredPermissions,
  userPermissions,
}) => {
  const [hasTrackedTooltip, setHasTrackedTooltip] = useState(false);
  const { analyticsEnhancedTrack } = useAnalytics();
  const hasAccess = requiredPermissions.some((perm) =>
    userPermissions.includes(perm)
  );

  return (
    <Tooltip
      onOpenChange={(open) => {
        if (!hasTrackedTooltip && !open) {
          setHasTrackedTooltip(true);
          analyticsEnhancedTrack("Data Asset Pro+ Tooltip Opened", {
            assetName,
            assetPermissions: requiredPermissions,
            postName,
          });
        }
      }}
    >
      <TooltipTrigger>
        <div data-testid={hasAccess ? "access" : "no-access"}>
          <Body
            as="span"
            bold
            className={cx(
              "mb-xxs rounded-3 border-1 px-xs uppercase",
              hasAccess
                ? "border-primary-300 bg-primary-100 text-primary-800"
                : "border-gray-700 bg-gray-200 text-gray-700"
            )}
            size="extraSmall"
          >
            Pro+
          </Body>
        </div>
      </TooltipTrigger>
      {hasAccess ? null : (
        <TooltipContent>
          <Body className="w-fit" size="small">
            This file requires one of the following permissions:
          </Body>
          <Body className="w-fit" size="small">
            <DataAccessTooltipLabel permissions={requiredPermissions} />. &nbsp;
            <Link
              className="text-white underline"
              onClick={() => {
                analyticsEnhancedTrack("Data Asset Pro+ Tooltip Link Clicked", {
                  assetName,
                  assetPermissions: requiredPermissions,
                  postName,
                });
              }}
              href="/enterprise-data-access"
            >
              Click here to learn more.
            </Link>
          </Body>
        </TooltipContent>
      )}
    </Tooltip>
  );
};
